import './DeviceBreakoutModal.scss';
import { ReactElement, useState } from 'react';
import * as Dfns from 'date-fns/fp';
import { ListGroup, Modal } from 'react-bootstrap';
import {
  formatMoney,
  formatMoneyAsInt,
  formatNumber
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DateBox } from '@Components/DateBox';
import { EntityKind, EntityLogo, EntitySize } from '@Components/EntityLogo';
import { Flight, ModalData } from '../device-breakout-constants';

const CANCELED_TIMESTAMP_FORMAT = 'MM/dd/yyy @ h:mm a';

const makeCanceledTimestamp = (canceled: string): string =>
  Dfns.format(CANCELED_TIMESTAMP_FORMAT)(Dfns.parseISO(canceled));

const makeCPM = (spend: number, impressions: number): number =>
  impressions ? Math.round((spend / impressions) * 10000) / 10 : 0;

interface DeviceBreakoutModalProps {
  data: ModalData;
  flightMap?: Record<string, Flight>;
  onClose: () => void;
}

interface FlightRowProps {
  flightId: string;
  flight: Flight;
  expanded: boolean;
  onToggle: () => void;
}

const FlightRow = ({ flightId, flight, expanded, onToggle }: FlightRowProps): ReactElement => (
  <ListGroup.Item action onClick={onToggle}>
    <div className='main-flight-row'>
      <div>
        <div className='flight-ids'>
          Flight #{flightId}
          <small>(Order #{flight.orderID})</small>
        </div>
        <DateBox start={flight.startDate} end={flight.endDate} />
      </div>
      <div>
        <div className='labeled'>
          <span>Spend:</span>
          {formatMoneyAsInt(flight.spend)}
        </div>
        <div className='labeled'>
          <span>CPM:</span>
          {flight.cpm ? formatMoney(flight.cpm) : '$0'}
        </div>
        <div className='labeled'>
          <span>Impressions:</span>
          {formatNumber(flight.impressions)}
        </div>
      </div>
    </div>
    {expanded && (
      <div className='expanded-section'>
        {flight.properties?.length ? (
          <div className='labeled'>
            <span>Properties:</span>
            {flight.properties.join(', ')}
          </div>
        ) : null}
        {flight.canceled && (
          <div className='labeled'>
            <span>Canceled:</span>
            {makeCanceledTimestamp(flight.canceled)}
          </div>
        )}
        {flight.overrides && (
          <div className='expanded-row'>
            <div className='labeled'>
              <span>
                Overrides <small>(spend/cpm/imps)</small>:
              </span>
              {formatMoneyAsInt(flight.overrides.spend)} /{' '}
              {formatMoney(makeCPM(flight.overrides.spend, flight.overrides.impressions))} /{' '}
              {formatNumber(flight.overrides.impressions)}
            </div>
          </div>
        )}
        {flight.derivedNetwork && (
          <div className='labeled'>
            <span>Derived Network:</span>
            {flight.derivedNetwork}
          </div>
        )}
        <div className='labeled'>
          <span>PDF Text</span>
        </div>
        <div className='pdf-text'>
          {flight.pdfText
            ? flight.pdfText.split('\n').map((text, i) => <p key={`${text}_${i}`}>{text || ' '}</p>)
            : 'None'}
        </div>
      </div>
    )}
  </ListGroup.Item>
);

export const DeviceBreakoutModal = ({
  data,
  flightMap,
  onClose
}: DeviceBreakoutModalProps): ReactElement => {
  const { flights, network, placement } = data;
  const [expandedFlightMap, setExpandedFlightMap] = useState<Record<string, boolean>>({});

  return (
    <Modal show onHide={onClose} dialogClassName='device-breakout-modal'>
      <Modal.Header closeButton>
        <Modal.Title className='modal-header'>
          {network && (
            <EntityLogo
              kind={EntityKind.NETWORK}
              id={network}
              size={EntitySize.XL}
              showFallbackName
            />
          )}
          <div className='header-name'>{placement}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='data-section'>
          <div className='data-label'>Flights</div>
          <div className='val-row'>
            <ListGroup className='flight-list'>
              {flights.map(flightId => {
                const flight = flightMap?.[flightId];
                if (!flight) {
                  return null;
                }
                return (
                  <FlightRow
                    key={flightId}
                    flightId={flightId}
                    flight={flight}
                    expanded={Boolean(expandedFlightMap[flightId])}
                    onToggle={() =>
                      setExpandedFlightMap(map => ({ ...map, [flightId]: !map[flightId] }))
                    }
                  />
                );
              })}
            </ListGroup>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
